/**
 * Linked Apps Registry (web)
 *
 * External SCA UK apps that ops-hub controls access to. A user's role for
 * each app lives on their TeamMember record (`appAccess`), assigned in
 * Team Management. The dashboard shows a launch tile for each app the
 * signed-in user can access.
 *
 * Launch URLs are deployment config: set VITE_APP_URL_<ID> (e.g.
 * VITE_APP_URL_MARATHON) in the web app's environment. Apps without a
 * configured URL still appear in Team Management but show no launch tile.
 */

export type AppId = 'marathon';
export type AppRole = 'admin' | 'scorer' | 'readonly';
export type AppAccess = Partial<Record<AppId, AppRole>>;

export interface LinkedApp {
  id: AppId;
  name: string;
  description: string;
  /** Roles this app supports, in order of decreasing access */
  roles: AppRole[];
  /** Launch URL from env; undefined when not configured for this environment */
  url?: string;
}

export const APP_ROLE_LABELS: Record<AppRole, string> = {
  admin: 'Admin',
  scorer: 'Scorer',
  readonly: 'Read only',
};

export const APP_ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  admin: 'Full access to everything in the app',
  scorer: 'Can score and view results; no settings or data management',
  readonly: 'Can view only',
};

export const LINKED_APPS: LinkedApp[] = [
  {
    id: 'marathon',
    name: 'London Marathon Shortlist',
    description:
      'Score marathon applicants, review the shortlist, and produce the trustee report',
    roles: ['admin', 'scorer', 'readonly'],
    url: import.meta.env.VITE_APP_URL_MARATHON || undefined,
  },
];

/** Apps the given access map grants any role for */
export function accessibleApps(appAccess: AppAccess | null | undefined): LinkedApp[] {
  if (!appAccess) return [];
  return LINKED_APPS.filter((app) => appAccess[app.id]);
}
